// src/rds_ui/api/designApi.js
//
// Migrated off raw fetch() onto the shared axios instance in
// src/api/client.js (see projectsApi.js in this folder for why). Return
// shapes and thrown error messages/shapes (including the .status property
// fetchDesignRun attaches, and the 404-means-null convention in
// fetchProjectLatestDesign) are preserved for existing callers.
import { api, getApiError } from "../../api/client";

const BASE = "/rds";

export async function startHLDGeneration(projectId, { sowId, generationRunId } = {}) {
  const body = {};
  if (sowId) body.sow_id = sowId;
  if (generationRunId) body.generation_run_id = generationRunId;
  try {
    const res = await api.post(`${BASE}/projects/${projectId}/design/generate`, body);
    return res.data;
  } catch {
    throw new Error("Failed to start HLD generation");
  }
}

export async function fetchDesignRun(runId) {
  try {
    const res = await api.get(`${BASE}/design/${runId}`);
    return res.data;
  } catch (e) {
    const err = new Error("Failed to fetch design run");
    err.status = e?.response?.status;
    throw err;
  }
}

export async function fetchProjectLatestDesign(projectId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/design/latest`);
    return res.data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw new Error("Failed to fetch design run");
  }
}

export async function fetchProjectDesignHistory(projectId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/design`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch design history");
  }
}

export async function fetchDesignArtifacts(runId) {
  try {
    const res = await api.get(`${BASE}/design/${runId}/artifacts`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch design artifacts");
  }
}

export async function fetchDesignArtifact(runId, sectionType) {
  try {
    const res = await api.get(`${BASE}/design/${runId}/artifacts/${sectionType}`);
    return res.data;
  } catch {
    throw new Error(`Failed to fetch section: ${sectionType}`);
  }
}

export async function updateDesignArtifact(runId, sectionType, contentMarkdown) {
  try {
    const res = await api.patch(`${BASE}/design/${runId}/artifacts/${sectionType}`, {
      content_markdown: contentMarkdown,
    });
    return res.data;
  } catch {
    throw new Error("Failed to save changes");
  }
}

export async function regenerateSection(runId, sectionType, instruction = null) {
  try {
    const res = await api.post(`${BASE}/design/${runId}/artifacts/${sectionType}/regenerate`, {
      instruction: instruction || null,
    });
    return res.data;
  } catch (e) {
    throw new Error(getApiError(e));
  }
}

export async function fetchSectionVersions(runId, sectionType) {
  try {
    const res = await api.get(`${BASE}/design/${runId}/artifacts/${sectionType}/versions`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch versions");
  }
}

export async function restoreSectionVersion(runId, sectionType, versionId) {
  try {
    const res = await api.post(`${BASE}/design/${runId}/artifacts/${sectionType}/restore/${versionId}`);
    return res.data;
  } catch {
    throw new Error("Failed to restore version");
  }
}

export async function deleteDesignRun(runId) {
  try {
    await api.delete(`${BASE}/design/${runId}`);
    return true;
  } catch {
    throw new Error("Failed to delete design run");
  }
}

export async function exportDesignRunDocx(runId, filename) {
  let blob;
  try {
    const res = await api.get(`${BASE}/design/${runId}/export/docx`, { responseType: "blob" });
    blob = res.data;
  } catch {
    throw new Error("Failed to export HLD");
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `HLD_run_${runId}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
